import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Prefix } from './models/prefix';
import { Observable, lastValueFrom, of, tap } from 'rxjs';
import { StateProvince } from './models/state-province';
import { OrganizationType } from './models/organization-type';
import { ReferenceData } from './models/reference-data';
import { ApplicationPurposeCodeEnum } from '../../../modules/application/shared/constants/application-purpose-codes';
import { OrderStatus } from '../../../modules/application/orders/shared/constants/order-status';
import { AdvisorUser } from '../user/models/advisor-user';
import { FiveDayHoldDate } from './models/five-day-hold-date';

@Injectable({
    providedIn: 'root',
})
export class ResourcesService {
    private readonly baseUrl = 'api/Resources';

    private referenceDataCache: Map<string, ReferenceData[]> = new Map();

    constructor(private readonly http: HttpClient) {}

    // TODO: cache civi data
    getPrefixes(): Observable<Prefix[]> {
        return this.http.get<Prefix[]>(`${this.baseUrl}/prefixes`);
    }

    getStateProvinces(): Observable<StateProvince[]> {
        return this.http.get<StateProvince[]>(
            `${this.baseUrl}/state-provinces`,
        );
    }

    getOrganizationTypes(): Observable<OrganizationType[]> {
        return this.http.get<OrganizationType[]>(
            `${this.baseUrl}/organization-types`,
        );
    }

    getApplicationPurposes(): Observable<ReferenceData[]> {
        return this.http.get<ReferenceData[]>(
            `${this.baseUrl}/application-purposes`,
        );
    }

    getApplicationStatuses(): Observable<ReferenceData[]> {
        return this.http.get<ReferenceData[]>(
            `${this.baseUrl}/application-statuses`,
        );
    }

    getFileExtensionWhitelist(): Observable<string[]> {
        return this.http.get<string[]>(
            `${this.baseUrl}/file-extension-whitelist`,
        );
    }

    getSwitchableApplicationPurposeCodes(): Observable<
        ApplicationPurposeCodeEnum[]
    > {
        return this.http.get<ApplicationPurposeCodeEnum[]>(
            `${this.baseUrl}/switchable-application-purpose-codes`,
        );
    }

    getPermittedOrderStatusTransitions(): Observable<
        Partial<Record<keyof typeof OrderStatus, OrderStatus[]>>
    > {
        return this.http.get<
            Partial<Record<keyof typeof OrderStatus, OrderStatus[]>>
        >(`${this.baseUrl}/permitted-order-status-transitions`);
    }

    getAdvisors(): Observable<AdvisorUser[]> {
        return this.http.get<AdvisorUser[]>(`${this.baseUrl}/advisors`);
    }

    getFiveDayHoldDates(): Observable<FiveDayHoldDate[]> {
        return this.http.get<FiveDayHoldDate[]>(
            `${this.baseUrl}/five-day-hold-dates`,
        );
    }

    getReferenceDataByKey(key: string): Observable<ReferenceData[]> {
        const cachedData = this.referenceDataCache.get(key);
        if (cachedData) {
            return of(cachedData);
        }

        const result = this.http.get<ReferenceData[]>(
            `${this.baseUrl}/get-by-key?key=${key}`,
        );

        return result.pipe(
            tap((data: ReferenceData[]) => {
                this.referenceDataCache.set(key, data);
            }),
        );
    }

    async getStateProvince(id: number): Promise<StateProvince | undefined> {
        const stateProvinces: StateProvince[] = await lastValueFrom(
            this.http.get<StateProvince[]>(`${this.baseUrl}/state-provinces`),
        );
        return stateProvinces.find((sp) => sp.id === id);
    }
}
